<template>
  <el-dialog
    :visible.sync="innerVisible"
    title="从其它阶段引入证据"
    width="780px"
    append-to-body
    :close-on-click-modal="false"
    :show-close="false"
    custom-class="stage-evidence-import-dialog"
    @close="onClose">
    <div class="seid-header">
      <h3 class="seid-title">从其它阶段引入证据</h3>
      <i class="el-icon-close seid-close" @click="handleCancel"></i>
    </div>
    <div class="seid-body">
      <div class="seid-hint">
        <i class="el-icon-info"></i>
        勾选题目 → 系统按申诉范围内的门店自动匹配提交的文件 → 引入到门店卡片的"门店级证据"区
      </div>

      <div v-if="loadingStages" class="seid-loading">
        <i class="el-icon-loading"></i> 正在加载阶段题目...
      </div>
      <div v-else-if="stages.length === 0" class="seid-empty">
        <i class="el-icon-document"></i>
        <p>该波次下没有其它阶段问卷</p>
      </div>
      <el-tabs v-else v-model="activeTab" type="card" class="seid-tabs">
        <el-tab-pane
          v-for="stage in stages"
          :key="stage.phaseNo"
          :label="`阶段${stage.phaseNo} ${stage.phaseName}`"
          :name="String(stage.phaseNo)">
          <div class="seid-stage-header">
            <el-checkbox
              :value="isStageAllSelected(stage)"
              :indeterminate="isStageIndeterminate(stage)"
              @change="toggleStageAll(stage, $event)">
              全选（{{ stage.items.length }} 题）
            </el-checkbox>
            <span class="seid-stage-count">已选 {{ countSelectedInStage(stage) }} 题</span>
          </div>
          <div class="seid-question-list">
            <div
              v-for="q in stage.items"
              :key="q.itemKey"
              class="seid-question-row"
              :class="{ 'seid-question-row-selected': selectedItemKeys.includes(q.itemKey) }">
              <el-checkbox :value="selectedItemKeys.includes(q.itemKey)" @change="toggleItem(q.itemKey, $event)">
                <span class="seid-question-serial" v-if="q.serialNo">{{ q.serialNo }}</span>
                <span class="seid-question-label">{{ q.itemLabel || q.itemKey }}</span>
                <span class="seid-question-type">{{ typeLabel(q.itemType) }}</span>
              </el-checkbox>
            </div>
          </div>
        </el-tab-pane>
      </el-tabs>

      <div class="seid-summary">
        <span>已选 {{ selectedItemKeys.length }} 题</span>
        <span v-if="selectedItemKeys.length > 0">· 预计最多引入 ~{{ estimatedFiles }} 个文件</span>
      </div>
    </div>
    <div class="seid-footer">
      <el-button @click="handleCancel" class="seid-cancel-btn">取消</el-button>
      <el-button
        type="primary"
        :loading="pulling"
        :disabled="selectedItemKeys.length === 0"
        @click="handleConfirm"
        class="seid-confirm-btn">
        确认引入
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import { getStageQuestionList, pullStageEvidence } from '@/app/index/api/project/appeal'

export default {
  name: 'StageEvidenceImportDialog',
  props: {
    visible: { type: Boolean, default: false },
    waveId: { type: [Number, String], default: null },
    appealFormKey: { type: String, default: '' },
    dealerCodes: { type: Array, default: () => [] }
  },
  data() {
    return {
      innerVisible: false,
      loadingStages: false,
      pulling: false,
      stages: [],
      activeTab: '',
      selectedItemKeys: []
    }
  },
  computed: {
    estimatedFiles() {
      // 粗略上限：题目数 × 门店数（实际命中可能远小于）
      return this.selectedItemKeys.length * Math.max(this.dealerCodes.length, 0)
    }
  },
  watch: {
    visible(v) {
      this.innerVisible = v
      if (v) this.loadStages()
    }
  },
  methods: {
    async loadStages() {
      this.stages = []
      this.selectedItemKeys = []
      if (!this.waveId) {
        this.$message.warning('缺少波次信息，无法加载阶段题目')
        return
      }
      this.loadingStages = true
      try {
        const stages = await getStageQuestionList(this.waveId, this.appealFormKey || '')
        this.stages = Array.isArray(stages) ? stages : []
        if (this.stages.length > 0) {
          this.activeTab = String(this.stages[0].phaseNo)
        }
      } catch (e) {
        this.$message.error(e?.response?.data?.message || e?.message || '加载阶段题目失败')
      } finally {
        this.loadingStages = false
      }
    },
    isStageAllSelected(stage) {
      return stage.items.length > 0 && stage.items.every(i => this.selectedItemKeys.includes(i.itemKey))
    },
    isStageIndeterminate(stage) {
      const n = stage.items.filter(i => this.selectedItemKeys.includes(i.itemKey)).length
      return n > 0 && n < stage.items.length
    },
    countSelectedInStage(stage) {
      return stage.items.filter(i => this.selectedItemKeys.includes(i.itemKey)).length
    },
    toggleStageAll(stage, checked) {
      const keys = stage.items.map(i => i.itemKey)
      if (checked) {
        const set = new Set(this.selectedItemKeys)
        keys.forEach(k => set.add(k))
        this.selectedItemKeys = Array.from(set)
      } else {
        const exclude = new Set(keys)
        this.selectedItemKeys = this.selectedItemKeys.filter(k => !exclude.has(k))
      }
    },
    toggleItem(itemKey, checked) {
      if (checked) {
        if (!this.selectedItemKeys.includes(itemKey)) this.selectedItemKeys.push(itemKey)
      } else {
        this.selectedItemKeys = this.selectedItemKeys.filter(k => k !== itemKey)
      }
    },
    typeLabel(itemType) {
      const t = (itemType || '').toUpperCase()
      if (t.includes('IMG') || t.includes('IMAGE') || t.includes('PHOTO')) return '图片'
      if (t.includes('AUDIO') || t.includes('RECORDING')) return '音频'
      if (t.includes('VIDEO')) return '视频'
      if (t.includes('SIGNATURE')) return '签名'
      if (t.includes('FILE')) return '文件'
      return ''
    },
    async handleConfirm() {
      if (this.selectedItemKeys.length === 0) return
      if (!this.dealerCodes || this.dealerCodes.length === 0) {
        this.$message.warning('当前申诉范围没有门店')
        return
      }
      this.pulling = true
      try {
        const result = await pullStageEvidence(this.waveId, this.dealerCodes, this.selectedItemKeys)
        // result 形如 [{dealerCode, dealerName, evidences: [{url, displayName, ...}]}, ...]
        // 统计有多少文件
        const fileCount = (Array.isArray(result) ? result : []).reduce(
          (sum, g) => sum + (g.evidences?.length || 0), 0)
        if (fileCount === 0) {
          this.$message.info('所选题目下没有可引入的文件')
          return
        }
        this.$emit('imported', result)
        this.$message.success(`已引入 ${fileCount} 个文件到门店卡片`)
        this.innerVisible = false
        this.$emit('update:visible', false)
      } catch (e) {
        this.$message.error(e?.response?.data?.message || e?.message || '引入失败')
      } finally {
        this.pulling = false
      }
    },
    handleCancel() {
      this.innerVisible = false
      this.$emit('update:visible', false)
    },
    onClose() {
      this.$emit('update:visible', false)
    }
  }
}
</script>

<style lang="scss" scoped>
/* 内容区样式（header/footer 在 Settings 全局样式里用 add-member-dialog 对齐）*/
</style>

<style lang="scss">
.stage-evidence-import-dialog.el-dialog {
  border-radius: 16px !important;
  overflow: hidden !important;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15) !important;
}
.stage-evidence-import-dialog .el-dialog__header { display: none; }
.stage-evidence-import-dialog .el-dialog__body { padding: 0; }

.seid-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px 12px;
  border-bottom: 1px solid #f0f0f0;
}
.seid-title { margin: 0; font-size: 17px; font-weight: 700; color: #1a1a1a; }
.seid-close {
  cursor: pointer; font-size: 18px; color: #c0c4cc;
  &:hover { color: #909399; }
}

.seid-body { padding: 16px 24px 0; min-height: 360px; max-height: 70vh; overflow-y: auto; }
.seid-hint {
  background: #fff9e6;
  border-left: 3px solid #f59e0b;
  border-radius: 4px;
  padding: 10px 12px;
  color: #9a6c00;
  font-size: 12px;
  margin-bottom: 14px;
  i { margin-right: 4px; }
}
.seid-loading, .seid-empty {
  text-align: center;
  padding: 60px 20px;
  color: #94a3b8;
  i { font-size: 40px; display: block; margin-bottom: 12px; }
}
.seid-stage-header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 8px 2px 10px;
  border-bottom: 1px dashed #f0f0f0;
  margin-bottom: 8px;
  .seid-stage-count { color: #6b7280; font-size: 12px; }
}
.seid-question-list { max-height: 380px; overflow-y: auto; }
.seid-question-row {
  padding: 8px 10px;
  border-radius: 6px;
  &:hover { background: #f5f7fa; }
}
.seid-question-row-selected { background: #eef4ff; }
.seid-question-serial {
  display: inline-block;
  min-width: 32px;
  padding: 1px 6px;
  margin-right: 8px;
  background: #e0e7ff;
  color: #4338ca;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 600;
}
.seid-question-label { color: #1a1a1a; font-size: 13px; }
.seid-question-type {
  margin-left: 8px;
  padding: 1px 6px;
  background: #f3f4f6;
  color: #6b7280;
  border-radius: 3px;
  font-size: 11px;
}
.seid-summary {
  margin-top: 12px;
  padding: 10px 0;
  border-top: 1px solid #f0f0f0;
  color: #6b7280;
  font-size: 12px;
}
.seid-footer {
  display: flex; justify-content: flex-end; gap: 10px;
  padding: 16px 24px 20px;
  border-top: 1px solid #f0f0f0;
}
.seid-cancel-btn, .seid-confirm-btn {
  border-radius: 20px !important;
  min-width: 84px !important;
  padding: 8px 20px !important;
}
.seid-confirm-btn {
  font-weight: 600 !important;
  background-color: #1476ff !important;
  border-color: #1476ff !important;
  &:hover, &:focus { background-color: #0d5fd9 !important; border-color: #0d5fd9 !important; }
}
</style>
